import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import { DataTable, StatusBadge, Skeleton } from '@connor-music/shared';
import type { Column } from '@connor-music/shared';
import { HardDrive, Database, DollarSign, AlertCircle, RefreshCw } from 'lucide-react';

interface BucketInfo {
  project_id: string;
  project_name: string;
  project_status: string;
  bucket_name: string;
  size_bytes: number;
  object_count: number;
  standard_bytes: number;
  glacier_bytes: number;
  storage_class: string;
  lifecycle_state: string;
  estimated_monthly_cost: number;
}

interface StorageTotals {
  total_size_bytes: number;
  total_objects: number;
  total_estimated_monthly_cost: number;
  active_buckets: number;
  expiring_buckets: number;
}

interface MonthlyCost {
  month: string;
  cost: number;
}

type BucketRow = BucketInfo & Record<string, unknown>;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatCost(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function formatMonth(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export default function AdminStorage() {
  const [buckets, setBuckets] = useState<BucketInfo[]>([]);
  const [totals, setTotals] = useState<StorageTotals | null>(null);
  const [monthlyCosts, setMonthlyCosts] = useState<MonthlyCost[]>([]);
  const [billingError, setBillingError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isBillingLoading, setIsBillingLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOverview = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await apiClient.getStorageOverview();
      setBuckets(data.buckets);
      setTotals(data.totals);
    } catch (err) {
      console.error('Failed to load storage overview:', err);
      setError('Failed to load storage overview.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBilling = async () => {
    setIsBillingLoading(true);
    setBillingError('');
    try {
      const data = await apiClient.getStorageBilling();
      if (data.error) {
        setBillingError(data.error);
      }
      setMonthlyCosts(data.monthly_costs || []);
    } catch (err) {
      console.error('Failed to load billing data:', err);
      setBillingError('Failed to load billing data from Cost Explorer.');
    } finally {
      setIsBillingLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
    loadBilling();
  }, []);

  const columns: Column<BucketRow>[] = [
    {
      key: 'project_name',
      label: 'Project',
      render: (_value, item) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {item.project_name as string}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate max-w-[200px]">
            {item.bucket_name as string}
          </p>
        </div>
      ),
    },
    {
      key: 'lifecycle_state',
      label: 'Status',
      render: (_value, item) => {
        const state = item.lifecycle_state as string;
        if (state === 'Active') {
          return <StatusBadge status="active" type="project" />;
        }
        return (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {state}
          </span>
        );
      },
    },
    {
      key: 'size_bytes',
      label: 'Size',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {formatBytes(value as number)}
        </span>
      ),
    },
    {
      key: 'object_count',
      label: 'Objects',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {(value as number).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'storage_class',
      label: 'Storage Class',
      render: (value) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {value as string}
        </span>
      ),
    },
    {
      key: 'estimated_monthly_cost',
      label: 'Est. Monthly',
      sortable: true,
      render: (value) => (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {formatCost(value as number)}
        </span>
      ),
    },
  ];

  // Find the max cost for the bar chart scale
  const maxCost = monthlyCosts.length > 0
    ? Math.max(...monthlyCosts.map((c) => c.cost), 0.01)
    : 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
            <HardDrive className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Storage</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              S3 bucket metrics, lifecycle status, and cost estimates.
            </p>
          </div>
        </div>
        <button
          onClick={() => { loadOverview(); loadBilling(); }}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-lg bg-white dark:bg-gray-800 p-4 shadow space-y-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-lg bg-white dark:bg-gray-800 p-6 shadow space-y-4">
              <Skeleton className="h-5 w-52" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-20 rounded-md" />
                <Skeleton className="h-20 rounded-md" />
              </div>
            </div>
            <div className="rounded-lg bg-white dark:bg-gray-800 p-6 shadow space-y-4">
              <Skeleton className="h-5 w-48" />
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full rounded" />
              ))}
            </div>
          </div>
          <Skeleton className="h-5 w-36" />
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded" />
            ))}
          </div>
        </div>
      ) : totals && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              icon={<Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
              label="Total Storage"
              value={formatBytes(totals.total_size_bytes)}
              bgClass="bg-blue-100 dark:bg-blue-900/50"
            />
            <SummaryCard
              icon={<DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />}
              label="Est. Monthly Cost"
              value={formatCost(totals.total_estimated_monthly_cost)}
              bgClass="bg-green-100 dark:bg-green-900/50"
            />
            <SummaryCard
              icon={<HardDrive className="h-5 w-5 text-primary-600 dark:text-primary-400" />}
              label="Active Buckets"
              value={String(totals.active_buckets)}
              bgClass="bg-primary-100 dark:bg-primary-900/50"
            />
            <SummaryCard
              icon={<HardDrive className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
              label="Pending Deletion"
              value={String(totals.expiring_buckets)}
              subtitle="expiring"
              bgClass="bg-amber-100 dark:bg-amber-900/50"
            />
          </div>

          {/* Cost Comparison */}
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Estimated vs Actual - Current Month */}
            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                Cost Comparison (Current Month)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Estimated</p>
                  <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                    {formatCost(totals.total_estimated_monthly_cost)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Based on bucket sizes
                  </p>
                </div>
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Actual (AWS)</p>
                  {isBillingLoading ? (
                    <Skeleton className="h-7 w-20 mt-1" />
                  ) : billingError ? (
                    <p className="text-sm text-amber-600 dark:text-amber-400">Unavailable</p>
                  ) : (
                    <p className="text-xl font-bold text-green-700 dark:text-green-300">
                      {monthlyCosts.length > 0
                        ? formatCost(monthlyCosts[monthlyCosts.length - 1].cost)
                        : '$0.00'}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    All account S3 usage
                  </p>
                </div>
              </div>
            </div>

            {/* Monthly S3 Costs (Bar Chart) */}
            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                AWS S3 Costs (Last 6 Months)
              </h3>
              {isBillingLoading ? (
                <div className="space-y-3 py-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-5 flex-1 rounded" />
                      <Skeleton className="h-4 w-12" />
                    </div>
                  ))}
                </div>
              ) : billingError ? (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-700 dark:text-amber-400">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{billingError}</span>
                </div>
              ) : monthlyCosts.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                  No billing data available.
                </p>
              ) : (
                <div className="space-y-2">
                  {monthlyCosts.map((entry) => (
                    <div key={entry.month} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                        {formatMonth(entry.month)}
                      </span>
                      <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 dark:bg-green-400 rounded-full transition-all"
                          style={{ width: `${Math.max((entry.cost / maxCost) * 100, 2)}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-16 text-right">
                        {formatCost(entry.cost)}
                      </span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic">
                    Actual costs reflect all S3 usage in this AWS account.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Per-Bucket Table */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Per-Bucket Details
            </h3>
            {buckets.length === 0 ? (
              <div className="rounded-lg bg-white p-8 shadow dark:bg-gray-800 text-center">
                <HardDrive className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No project buckets found.
                </p>
              </div>
            ) : (
              <DataTable<BucketRow>
                columns={columns}
                data={buckets as BucketRow[]}
                emptyMessage="No buckets found."
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  subtitle,
  bgClass,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  bgClass: string;
}) {
  return (
    <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bgClass}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}
